import { useState } from "react";
import { LogOut, TrendingUp, ShoppingCart, Ticket, Loader2, ArrowLeftRight, CheckCircle2, XCircle, Swords } from "lucide-react";
import { CoinIcon, SokoCoinIcon } from "@/app/components/SvgIcons";
import { User } from "@/types/user";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/lib/axios";
import ExchangeModal from "@/app/components/ExchangeModal";
import { UserStatistics } from "@/types/userStatistics";

interface OverviewTabProps {
    user: User;
    onLogout: () => void;
    isKickProvider: boolean;
}

type RedeemResult = { ok: boolean; title: string; detail?: string };

export function OverviewTab({ user, onLogout, isKickProvider }: OverviewTabProps) {
    const [code, setCode] = useState("");
    const [redeem, setRedeem] = useState<RedeemResult | null>(null);
    const [isRedeeming, setIsRedeeming] = useState(false);
    const [showExchange, setShowExchange] = useState(false);
    const queryClient = useQueryClient();

    const { data: statistics, isLoading, isError, refetch: refetchStatistics } = useQuery<UserStatistics>({
        queryKey: ["userStatistics"],
        queryFn: async () => (await api.get("profile/statistics")).data.data,
        staleTime: 60000,
        refetchOnWindowFocus: true,
        enabled: isKickProvider,
    });

    const handleRedeem = async () => {
        if (!code.trim()) {
            setRedeem({ ok: false, title: "Enter a code first." });
            return;
        }

        setIsRedeeming(true);
        setRedeem(null);

        try {
            const response = await api.post("promo", { promo_code: code.trim() });

            if (response.data.status) {
                const pointsEarned: number = response.data.data;
                setRedeem({
                    ok: true,
                    title: `${pointsEarned.toLocaleString()} Kick Points added`,
                    detail: `Code “${code.trim().toUpperCase()}” redeemed — your balance is already updated.`,
                });
                setCode("");
                refetchStatistics();
                queryClient.invalidateQueries({ queryKey: ["user"] });
            } else {
                setRedeem({ ok: false, title: response.data.data || "That code isn’t valid." });
                setCode("");
            }
        } catch (error: unknown) {
            // 400s are validation answers (used code, expired, wrong code), not failures.
            const axiosError = error as { response?: { status?: number; data?: { data?: string; message?: string } } };
            const apiMessage = axiosError.response?.data?.data || axiosError.response?.data?.message;

            if (axiosError.response?.status !== 400) {
                console.error("Error redeeming promo code:", error);
            }

            setRedeem({
                ok: false,
                title: apiMessage || (axiosError.response?.status === 400
                    ? "That code isn’t valid."
                    : "Couldn’t reach the server. Try again in a moment."),
            });
            setCode("");
        } finally {
            setIsRedeeming(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !isRedeeming) handleRedeem();
    };

    const statValue = (value: number | undefined) => {
        if (isLoading) return <span className="text-gray-600 text-lg">—</span>;
        if (isError) return <span className="text-red-400 text-xs">Unavailable</span>;
        return (value ?? 0).toLocaleString();
    };

    return (
        <div className="space-y-4">
            {/* ---------- Balances ---------- */}
            {isKickProvider && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {user.points !== undefined && (
                        <BalanceCard
                            label="Kick Points"
                            value={user.points.toLocaleString()}
                            icon={<CoinIcon size={18} className="text-green-400" />}
                            accent="text-green-400"
                        />
                    )}
                    <BalanceCard
                        label="Soko Points"
                        value={(user.soko_points ?? 0).toLocaleString()}
                        icon={<SokoCoinIcon size={18} />}
                        accent="text-emerald-400"
                    />
                </div>
            )}

            {/* ---------- Exchange ---------- */}
            {isKickProvider && (
                <button
                    onClick={() => setShowExchange(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-800/50 text-emerald-400 hover:bg-emerald-500/10 transition-colors text-xs font-semibold uppercase tracking-wide"
                >
                    Exchange Kick Points <ArrowLeftRight size={14} /> Soko Points
                </button>
            )}

            {/* ---------- Lifetime stats ---------- */}
            {isKickProvider && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <MiniStat
                        label="Kick Points earned"
                        icon={<TrendingUp size={16} className="text-green-400" />}
                        value={statValue(statistics?.total_earnings)}
                        coin={<CoinIcon size={14} className="text-green-400" />}
                        accent="text-green-400"
                    />
                    <MiniStat
                        label="Soko Points wagered"
                        icon={<Swords size={16} className="text-emerald-400" />}
                        value={statValue(statistics?.total_soko_wagered)}
                        coin={<SokoCoinIcon size={14} />}
                        accent="text-emerald-400"
                    />
                    <MiniStat
                        label="Total orders"
                        icon={<ShoppingCart size={16} className="text-blue-400" />}
                        value={statValue(statistics?.total_orders)}
                        accent="text-white"
                    />
                </div>
            )}

            {/* ---------- Redeem code ---------- */}
            {isKickProvider && (
                <div className="rounded-xl bg-gray-800/50 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Ticket size={16} className="text-yellow-400" />
                        <h2 className="text-sm font-bold uppercase tracking-wide text-yellow-400">Redeem code</h2>
                    </div>
                    <p className="text-gray-400 text-xs mb-3">
                        Drop a promo or reward code here to claim Kick Points.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-2">
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="SOKO-XXXX"
                            disabled={isRedeeming}
                            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm font-mono text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition placeholder-gray-600 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider"
                        />
                        <button
                            onClick={handleRedeem}
                            disabled={isRedeeming || !code.trim()}
                            className="bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-lg shadow-lg hover:shadow-emerald-500/30 transition-all duration-200 px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isRedeeming ? <><Loader2 size={14} className="animate-spin" /> Redeeming</> : 'Redeem'}
                        </button>
                    </div>

                    {redeem && (
                        <div className={`mt-3 flex items-start gap-2.5 rounded-lg p-3 text-sm ${
                            redeem.ok ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'
                        }`}>
                            {redeem.ok
                                ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
                                : <XCircle size={16} className="shrink-0 mt-0.5" />}
                            <div className="min-w-0">
                                <div className="font-semibold">{redeem.title}</div>
                                {redeem.detail && <div className="text-xs opacity-80 mt-0.5 leading-relaxed">{redeem.detail}</div>}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ---------- Logout ---------- */}
            <button
                onClick={onLogout}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gray-800/50 text-red-400 hover:bg-red-500/10 transition-colors text-xs font-semibold uppercase tracking-wide"
            >
                <LogOut size={14} />
                Logout
            </button>

            {showExchange && <ExchangeModal onClose={() => setShowExchange(false)} />}
        </div>
    );
}

function BalanceCard({ label, value, icon, accent }: {
    label: string;
    value: string;
    icon: React.ReactNode;
    accent: string;
}) {
    return (
        <div className="rounded-xl bg-gray-800/50 p-4">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {icon}
                {label}
            </div>
            <div className={`mt-1.5 font-mono font-bold text-2xl tabular-nums ${accent}`}>{value}</div>
        </div>
    );
}

function MiniStat({ label, icon, value, coin, accent }: {
    label: string;
    icon: React.ReactNode;
    value: React.ReactNode;
    coin?: React.ReactNode;
    accent: string;
}) {
    return (
        <div className="rounded-xl bg-gray-800/50 p-4">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {icon}
                {label}
            </div>
            <div className={`mt-1.5 flex items-center gap-1.5 font-mono font-bold text-xl tabular-nums ${accent}`}>
                {value}
                {coin}
            </div>
        </div>
    );
}
